import { sumBy } from 'lodash';
import React, { useMemo } from 'react';
import { Box } from '../../../../../../../CommonComponents/OPL/Box/Box';
import { StackedBarData } from '../../../../../../../CommonComponents/OPL/charts/StackedBar/StackedBar';
import { Dot } from '../../../../../../../CommonComponents/OPL/Dot/Dot';
import { ModalBody } from '../../../../../../../CommonComponents/OPL/Modal/ModalBody';
import { Text } from '../../../../../../../CommonComponents/OPL/Text/Text';
import { Tooltip } from '../../../../../../../CommonComponents/OPL/Tooltip/Tooltip';
import { formatCurrency, formatPercentage } from '../../../../../../../CommonUtils/formatNumber';
import { BucketData } from '../../../useAssetBucketingResults';
import { NetWorthMakeup } from '../../NetWorthMakeup/NetWorthMakeup';
import { AssetsTable } from '../AssetsTable/AssetsTable';

interface OverviewTabProps {
  buckets: BucketData[];
  networthAmount?: number;
  networthMakeup: StackedBarData;
}

export const OverviewTab: React.FC<OverviewTabProps> = ({
  buckets,
  networthAmount,
  networthMakeup,
}) => {
  const allTotal = useMemo(() => sumBy(buckets, bucket => Math.abs(bucket.total)), [buckets]);

  return (
    <>
      <ModalBody>
        <Box display="flex" justifyContent="space-between" mb="8">
          <Text fontWeight={500}>Your Net Worth</Text>
          <Text fontWeight={500} color="neutral.500" variant="tabular">
            {networthAmount ? formatCurrency(networthAmount) : '–'}
          </Text>
        </Box>

        <NetWorthMakeup data={networthMakeup} />
      </ModalBody>

      <AssetsTable minHeight={500}>
        {buckets.map(bucket => {
          return (
            <React.Fragment key={bucket.title}>
              <tr>
                <Box as="td" display="flex" alignItems="center">
                  <Dot size={8} bg={bucket.mainColor} mr="4" />
                  <Text fontWeight={600}>{bucket.title}</Text>
                </Box>

                <Text as="td" fontWeight={600} variant="tabular" textAlign="right">
                  {allTotal !== 0
                    ? formatPercentage(Math.abs(bucket.total / allTotal), { decimalPlaces: 2 })
                    : ''}
                </Text>

                <Text as="td" fontWeight={600} variant="tabular" textAlign="right">
                  {formatCurrency(bucket.total)}
                </Text>
              </tr>

              {bucket.data.map(({ name, assets, y, displayValue }) => {
                if (!assets) {
                  return null;
                }

                return (
                  <React.Fragment key={name}>
                    <tr>
                      <td colSpan={assets.length !== 1 ? 1 : 3}>
                        <Text fontWeight={500} color="neutral.500" ml="12">
                          {name}
                        </Text>
                      </td>

                      {assets.length !== 1 && (
                        <>
                          <Text
                            as="td"
                            fontWeight={500}
                            color="neutral.500"
                            variant="tabular"
                            textAlign="right"
                          >
                            {allTotal
                              ? formatPercentage(Math.abs(y / allTotal), { decimalPlaces: 2 })
                              : ''}
                          </Text>

                          {displayValue !== null && (
                            <Text
                              as="td"
                              fontWeight={600}
                              color="neutral.500"
                              variant="tabular"
                              textAlign="right"
                            >
                              {formatCurrency(displayValue)}
                            </Text>
                          )}
                        </>
                      )}
                    </tr>

                    {assets.map(asset => {
                      const hasDash = allTotal && Math.abs(asset.networthValue / allTotal) === 0;

                      return (
                        <tr key={asset.name}>
                          <td style={{ paddingLeft: 36 }}>
                            <Tooltip content={asset.name} isOverflowWrap>
                              <Text
                                fontWeight={500}
                                css={`
                                  white-space: nowrap;
                                  overflow: hidden;
                                  text-overflow: ellipsis;
                                `}
                              >
                                {asset.name}
                              </Text>
                            </Tooltip>
                            {asset.institutionName && (
                              <Text fontSize="small" color="neutral.600">
                                {asset.institutionName}
                              </Text>
                            )}
                          </td>

                          {allTotal && (
                            <Text
                              as="td"
                              fontWeight={500}
                              color="neutral.500"
                              variant="tabular"
                              textAlign={hasDash ? 'center' : 'right'}
                            >
                              {hasDash
                                ? '-'
                                : formatPercentage(Math.abs(asset.networthValue / allTotal), {
                                    decimalPlaces: 2,
                                  })}
                            </Text>
                          )}

                          <Text
                            as="td"
                            fontWeight={600}
                            color="neutral.500"
                            variant="tabular"
                            textAlign={asset.displayValue === null ? 'center' : 'right'}
                          >
                            {asset.displayValue === null ? '-' : formatCurrency(asset.displayValue)}
                          </Text>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </React.Fragment>
          );
        })}
      </AssetsTable>
    </>
  );
};
